import type { FC } from 'react'
import styled from 'styled-components'
import { useNavigate } from 'react-router'
import { useAppInsightsContext, useTrackEvent } from '@microsoft/applicationinsights-react-js'
import { PANO_LABELS, type PanoramaItem } from './constants'
import { PANO_LAYERS } from './PanoramaViewer'
import { isAuthenticated } from '../../../../shared/utils/auth/auth'
import { toGeoSearch } from '../../../../links'
import { panoTagParam, mapLayersParam } from '../../query-params'
import Icon from '../../../../shared/components/Icon/Icon'
import Checkmark from '../../../../shared/assets/icons/checkmark.svg'
import useParam from '../../../../shared/hooks/useParam'
import useBuildQueryString from '../../../../shared/hooks/useBuildQueryString'
import ContextMenuItem from '../../../../shared/components/ContextMenu/ContextMenuItem'

const CheckmarkIcon = styled(Icon)`
  padding-left: 0.75rem;
`

export const FontStyledContextMenuItem = styled(ContextMenuItem)`
  font-size: 1rem;
  line-height: 1.25;
  height: 34px;
  display: flex;
  align-items: center;
  width: 100%;

  &:hover {
    background: var(--greyUltraLight);
    cursor: pointer;
  }
`

const StyledContextMenuItem = styled(FontStyledContextMenuItem)`
  justify-content: flex-start;
  padding-left: 1.5rem;
  & > span:first-of-type {
    padding-left: 0;
    margin: 0.25rem 0.25rem 0.25rem -1rem;
  }
`

const PanoramaMenuItem: FC<{
  active: boolean
  item: PanoramaItem
  index: number
  onSelect: () => void
}> = ({ active, item, index, onSelect }) => {
  const { id, label } = item
  const [activeLayers] = useParam(mapLayersParam)
  const navigate = useNavigate()
  const { buildQueryString } = useBuildQueryString()
  const appInsights = useAppInsightsContext()
  const trackEvent = useTrackEvent(appInsights, 'SELECT_PANORAMA_LAYER', {})

  const handleClick = () => {
    const activeLayersWithoutPano =
      activeLayers.filter((activeLayer) => !PANO_LAYERS.includes(activeLayer)) ?? []

    if (isAuthenticated()) {
      trackEvent({
        category: 'panorama',
        layer: id,
      })
    }

    const { pathname } = toGeoSearch()
    const queryString = buildQueryString([
      [panoTagParam, id],
      [
        // @ts-ignore
        mapLayersParam,
        // @ts-ignore
        id === PANO_LABELS[0].id
          ? [...activeLayersWithoutPano, PANO_LAYERS[0]]
          : [...activeLayersWithoutPano, `pano-${id}`],
      ],
    ])

    navigate(`${pathname as string}?${queryString}`)
    onSelect()
  }

  return (
    <StyledContextMenuItem
      $divider={index === PANO_LABELS.length - 1}
      role="button"
      onClick={handleClick}
    >
      {active ? (
        <CheckmarkIcon inline size={12}>
          <Checkmark />
        </CheckmarkIcon>
      ) : null}
      {label}
    </StyledContextMenuItem>
  )
}

export default PanoramaMenuItem
